import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { medicamentService } from '../../services/api';
import { Medicament, OrdonnanceMedicament } from '../../types';
import { Plus, Search, X, AlertTriangle } from 'lucide-react';

interface MedicamentSelectorProps {
  medicamentsOrdonnance: Omit<OrdonnanceMedicament, 'id' | 'ordonnance'>[];
  onMedicamentsChange: (medicaments: Omit<OrdonnanceMedicament, 'id' | 'ordonnance'>[]) => void;
}

const MedicamentSelector: React.FC<MedicamentSelectorProps> = ({
  medicamentsOrdonnance,
  onMedicamentsChange
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  // Mini-saisie d'un médicament absent du catalogue (nom + dosage)
  const [nouveau, setNouveau] = useState<{ nom: string; dosage_standard: string } | null>(null);
  const queryClient = useQueryClient();

  // Recherche côté serveur : l'API est paginée, filtrer la 1re page ne suffirait pas
  const terme = searchTerm.trim();
  const { data: medicamentsResponse, isLoading } = useQuery({
    queryKey: ['medicaments', terme],
    queryFn: () => medicamentService.getAll(terme ? { search: terme } : undefined).then(res => res.data),
    keepPreviousData: true
  });

  const filteredMedicaments = medicamentsResponse?.results || [];

  const creation = useMutation({
    mutationFn: (data: { nom: string; dosage_standard: string }) =>
      medicamentService.create(data).then(res => res.data),
    onSuccess: (medicament: Medicament) => {
      queryClient.invalidateQueries({ queryKey: ['medicaments'] });
      setNouveau(null);
      ajouterMedicament(medicament);
    }
  });

  const creerMedicament = () => {
    if (!nouveau || !nouveau.nom.trim() || !nouveau.dosage_standard.trim()) return;
    creation.mutate({ nom: nouveau.nom.trim(), dosage_standard: nouveau.dosage_standard.trim() });
  };

  // Entrée valide la mini-saisie sans soumettre le formulaire de consultation
  const surEntree = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      creerMedicament();
    }
  };

  const ajouterMedicament = (medicament: Medicament) => {
    // Vérifier si le médicament n'est pas déjà dans la liste
    const existeDeja = medicamentsOrdonnance.some(m => m.medicament === medicament.id);
    if (existeDeja) {
      alert('Ce médicament est déjà dans l\'ordonnance');
      return;
    }

    const nouveauMedicament: Omit<OrdonnanceMedicament, 'id' | 'ordonnance'> = {
      medicament: medicament.id,
      medicament_details: medicament,
      dosage: medicament.dosage_standard,
      frequence: '',
      duree: '',
      instructions: '',
      quantite: ''
    };

    onMedicamentsChange([...medicamentsOrdonnance, nouveauMedicament]);
    setSearchTerm('');
    setShowSearch(false);
  };

  const supprimerMedicament = (index: number) => {
    const nouveauxMedicaments = medicamentsOrdonnance.filter((_, i) => i !== index);
    onMedicamentsChange(nouveauxMedicaments);
  };

  const modifierMedicament = (index: number, field: keyof Omit<OrdonnanceMedicament, 'id' | 'ordonnance' | 'medicament' | 'medicament_details'>, value: string) => {
    const nouveauxMedicaments = [...medicamentsOrdonnance];
    nouveauxMedicaments[index] = {
      ...nouveauxMedicaments[index],
      [field]: value
    };
    onMedicamentsChange(nouveauxMedicaments);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-900">Médicaments prescrits</h4>
        <button
          type="button"
          onClick={() => setShowSearch(true)}
          className="flex items-center px-3 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700"
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter un médicament
        </button>
      </div>

      {/* Recherche de médicaments */}
      {showSearch && (
        <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
          <div className="flex justify-between items-center mb-3">
            <h5 className="font-medium text-gray-900">Rechercher un médicament</h5>
            <button
              type="button"
              aria-label="Fermer la recherche"
              onClick={() => { setShowSearch(false); setNouveau(null); }}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou forme..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setNouveau(null); }}
              className="w-full pl-10 pr-4 py-2 text-gray-700 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto">
              {filteredMedicaments.map((medicament) => (
                <div
                  key={medicament.id}
                  className="flex justify-between items-center p-3 border border-gray-200 rounded-md mb-2 bg-white hover:bg-gray-50 cursor-pointer"
                  onClick={() => ajouterMedicament(medicament)}
                >
                  <div>
                    <div className="font-medium text-gray-900">{medicament.nom}</div>
                    <div className="text-sm text-gray-600">
                      {[medicament.forme, medicament.dosage_standard].filter(Boolean).join(' - ')}
                    </div>
                    {medicament.indication && (
                      <div className="text-xs text-blue-600">{medicament.indication}</div>
                    )}
                  </div>
                  <Plus className="h-4 w-4 text-blue-500" />
                </div>
              ))}
              {terme && filteredMedicaments.length === 0 && (
                <div className="text-center py-2 text-gray-500">
                  Aucun médicament trouvé
                </div>
              )}
            </div>
          )}

          {/* Toujours proposé : le nom peut exister avec un autre dosage */}
          {terme && !nouveau && (
            <button
              type="button"
              onClick={() => { creation.reset(); setNouveau({ nom: terme, dosage_standard: '' }); }}
              className="w-full flex items-center justify-center px-3 py-2 mt-2 text-sm text-teal-700 border border-dashed border-teal-400 rounded-md bg-white hover:bg-teal-50"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter « {terme} »
            </button>
          )}

          {nouveau && (
            <div className="mt-2 p-3 border border-teal-200 rounded-md bg-white space-y-2">
              <div className="text-sm font-medium text-gray-900">Nouveau médicament</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <label htmlFor="nouveau-med-nom" className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                  <input
                    id="nouveau-med-nom"
                    type="text"
                    value={nouveau.nom}
                    onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })}
                    onKeyDown={surEntree}
                    className="w-full px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label htmlFor="nouveau-med-dosage" className="block text-xs font-medium text-gray-700 mb-1">Dosage *</label>
                  <input
                    id="nouveau-med-dosage"
                    type="text"
                    autoFocus
                    value={nouveau.dosage_standard}
                    onChange={(e) => setNouveau({ ...nouveau, dosage_standard: e.target.value })}
                    onKeyDown={surEntree}
                    placeholder="Ex: 500mg"
                    className="w-full px-2 py-1 text-sm text-gray-700 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              {creation.isError && (
                <p className="text-xs text-red-600">Le médicament n'a pas pu être enregistré. Réessayez.</p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setNouveau(null)}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={creerMedicament}
                  disabled={creation.isLoading || !nouveau.nom.trim() || !nouveau.dosage_standard.trim()}
                  className="px-3 py-1 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50"
                >
                  {creation.isLoading ? 'Enregistrement…' : 'Créer et ajouter'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste des médicaments sélectionnés */}
      <div className="space-y-4">
        {medicamentsOrdonnance.map((medicamentOrd, index) => (
          <div key={index} className="border border-gray-200 rounded-xl p-4 bg-white">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h5 className="font-medium text-gray-900">
                  {medicamentOrd.medicament_details?.nom}
                </h5>
                <p className="text-sm text-gray-600">
                  {[medicamentOrd.medicament_details?.forme, medicamentOrd.medicament_details?.dosage_standard].filter(Boolean).join(' - ')}
                </p>
                {medicamentOrd.medicament_details?.contre_indication && (
                  <div className="flex items-center mt-1">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mr-1" />
                    <p className="text-xs text-amber-700">
                      {medicamentOrd.medicament_details.contre_indication}
                    </p>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => supprimerMedicament(index)}
                className="text-red-500 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Dosage */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Dosage *
                </label>
                <input
                  type="text"
                  value={medicamentOrd.dosage}
                  onChange={(e) => modifierMedicament(index, 'dosage', e.target.value)}
                  placeholder="Ex: 1 comprimé"
                  className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              {/* Fréquence */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Fréquence *
                </label>
                <input
                  type="text"
                  value={medicamentOrd.frequence}
                  onChange={(e) => modifierMedicament(index, 'frequence', e.target.value)}
                  placeholder="Ex: 3 fois par jour"
                  className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              {/* Durée */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Durée *
                </label>
                <input
                  type="text"
                  value={medicamentOrd.duree}
                  onChange={(e) => modifierMedicament(index, 'duree', e.target.value)}
                  placeholder="Ex: 7 jours"
                  className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              {/* Quantité */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Quantité
                </label>
                <input
                  type="text"
                  value={medicamentOrd.quantite}
                  onChange={(e) => modifierMedicament(index, 'quantite', e.target.value)}
                  placeholder="Ex: 1 boîte"
                  className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Instructions particulières
              </label>
              <textarea
                value={medicamentOrd.instructions}
                onChange={(e) => modifierMedicament(index, 'instructions', e.target.value)}
                placeholder="Ex: Prendre après les repas, avec un grand verre d'eau..."
                rows={2}
                className="w-full px-2 py-1 text-sm text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
        ))}

        {medicamentsOrdonnance.length === 0 && (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl">
            <Plus className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p>Aucun médicament ajouté à l'ordonnance</p>
            <p className="text-sm">Cliquez sur "Ajouter un médicament" pour commencer</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicamentSelector;